import { selector } from 'recoil';
import yaml from 'js-yaml';
import { appConfigState } from './appConfigAtoms';
import { cfdConfigState } from './cfdConfigAtoms';
import { parameterListState } from 'features/morphing/state/parameterAtoms';
import { cognitoIdState } from 'features/auth/state/authAtoms';
import { displayCocoonState, displayModelState } from 'features/file-browser/state/fileAtoms';

/* 日時情報の取得 */
const currentDate = new Date();
const formattedDate = currentDate.toLocaleString('ja-JP', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

export const yamlConfigState = selector<string>({
  key: 'yamlConfigState',
  get: ({ get }) => {
    const appConfig = get(appConfigState);
    const displayModel = get(displayModelState);
    const displayCocoon = get(displayCocoonState);
    const cfdConfig = get(cfdConfigState);
    const cognitoId = get(cognitoIdState);
    const parameterList = get(parameterListState);

    let displayModelPath = '';
    let tirePath = '';
    let wheelhousePath = '';
    if (displayModel) {
      displayModelPath = displayModel.path;
      const basePath = displayModelPath.replace(/\.[^/.]+$/, "");
      tirePath = `${basePath.replace('/model/', '/tire/')}`;
      wheelhousePath = `${basePath.replace('/model/', '/wheelhouse/')}`;
    }

    // Create the YAML config
    const config = {
      cfd: {
        date: formattedDate,
        user_id: cognitoId,
        car_type: cfdConfig.carType,
        openfoam_version: cfdConfig.openfoamVersion,
        cfd_model: cfdConfig.model,
        velocity: cfdConfig.velocity,
        note: cfdConfig.note,
      },
      application: {
        name: appConfig.name,
        study_name: appConfig.studyName,
        base_stl: displayModelPath,
        cocoon_stl: displayCocoon?.path || '',
        tire_stl: tirePath,
        wheelhouse_stl: wheelhousePath,
        n_parallels: appConfig.nParallels,
        n_trials: appConfig.nTrials,
        design_space: {
          max: [
            appConfig.maxDesignSpace.x,
            appConfig.maxDesignSpace.y,
            appConfig.maxDesignSpace.z,
          ],
          min: [
            appConfig.minDesignSpace.x,
            appConfig.minDesignSpace.y,
            appConfig.minDesignSpace.z,
          ],
        },
        morphing_parameters: parameterList.map((p) => {
          const targetControlPoints = [
            {
              x: [p.targetControlPointsX.start - 1, p.targetControlPointsX.stop],
              y: [p.targetControlPointsY.start - 1, p.targetControlPointsY.stop],
              z: [p.targetControlPointsZ.start - 1, p.targetControlPointsZ.stop],
            },
          ];

          // Add the second element for y-symmetry if ySymmetry is true
          if (p.ySymmetry) {
            targetControlPoints.push({
              x: [p.targetControlPointsX.start - 1, p.targetControlPointsX.stop],
              y: [
                p.controlPoints.y - p.targetControlPointsY.stop,
                p.controlPoints.y - p.targetControlPointsY.start + 1,
              ],
              z: [p.targetControlPointsZ.start - 1, p.targetControlPointsZ.stop],
            });
          }

          return {
            name: p.name,
            upper_bound: p.morphingParameter.upper,
            lower_bound: p.morphingParameter.lower,
            control_points: [p.controlPoints.x, p.controlPoints.y, p.controlPoints.z],
            bbox_origin: [p.morphingMin.x, p.morphingMin.y, p.morphingMin.z],
            bbox_length: [
              p.morphingMax.x - p.morphingMin.x,
              p.morphingMax.y - p.morphingMin.y,
              p.morphingMax.z - p.morphingMin.z,
            ],
            direction: p.morphingDirection,
            y_symmetry: p.ySymmetry,
            target_control_points: targetControlPoints,
          };
        }),
      },
    };

    // Convert the config object to YAML format
    const yamlData = yaml.dump(config);
    return yamlData;
  },
  set: ({ set }, newValue) => {
    if (typeof newValue === 'string') {
      try {
        const parsedConfig: any = yaml.load(newValue);

        // Extract values from the parsed YAML and update the respective atoms
        set(appConfigState, {
          name: parsedConfig.application.name,
          studyName: parsedConfig.application.study_name,
          nParallels: parsedConfig.application.n_parallels as number,
          nTrials: parsedConfig.application.n_trials as number,
          minDesignSpace: {
            x: parsedConfig.application.design_space.min[0],
            y: parsedConfig.application.design_space.min[1],
            z: parsedConfig.application.design_space.min[2],
          },
          maxDesignSpace: {
            x: parsedConfig.application.design_space.max[0],
            y: parsedConfig.application.design_space.max[1],
            z: parsedConfig.application.design_space.max[2],
          },
        });

        // set(cfdConfigState, {
        //   carType: parsedConfig.cfd.car_type,
        //   openfoamVersion: parsedConfig.cfd.openfoam_version,
        //   model: parsedConfig.cfd.cfd_model,
        //   velocity: parsedConfig.cfd.velocity as number,
        //   note: parsedConfig.cfd.note,
        // });

        // Set the morphing parameters
        const updatedParameterList = parsedConfig.application.morphing_parameters.map(
          (p: any) => ({
            name: p.name,
            controlPoints: {
              x: p.control_points[0],
              y: p.control_points[1],
              z: p.control_points[2],
            },
            morphingMin: {
              x: p.bbox_origin[0],
              y: p.bbox_origin[1],
              z: p.bbox_origin[2],
            },
            morphingMax: {
              x: p.bbox_origin[0] + p.bbox_length[0],
              y: p.bbox_origin[1] + p.bbox_length[1],
              z: p.bbox_origin[2] + p.bbox_length[2],
            },
            ySymmetry: p.y_symmetry as boolean,
            targetControlPointsX: {
              start: p.target_control_points[0].x[0] + 1,
              stop: p.target_control_points[0].x[1],
            },
            targetControlPointsY: {
              start: p.target_control_points[0].y[0] + 1,
              stop: p.target_control_points[0].y[1],
            },
            targetControlPointsZ: {
              start: p.target_control_points[0].z[0] + 1,
              stop: p.target_control_points[0].z[1],
            },
            morphingDirection: p.direction,
            morphingParameter: {
              upper: p.upper_bound,
              lower: p.lower_bound,
            },
          })
        );

        // Update the parameterListState atom with the new values
        set(parameterListState, updatedParameterList);
      } catch (error) {
        console.error('Failed to parse YAML:', error);
      }
    }
  },
});
